import type { IGetActionsData } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import { formatTimeFromDate } from "@candidate/components/utils/formatTools";
import { ReminderStatusIcon } from "@data-display/ReminderStatus";
import { Stack, Typography, useTheme } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { ExternalIntegrationStatusLabel } from "@root/styleguide/components/data-display/ExternalIntegrationStatusLabel";
import { useI18nContext } from "@root/utils/organisation";
import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import { classes } from "../styles";
import { ActionCardTitleDisplay, type IActionPopoverState } from "../types";

export interface IActionTitleProps {
  action: IGetActionsData;
  groupedActions: IGetActionsData[];
  title: string;
  actionTypeLabel: string;
  candidate: Pick<ICandidate, "firstName" | "lastName" | "_id"> | null;
  setActionPopoverState?: Dispatch<SetStateAction<IActionPopoverState>>;
  titleDisplay?: ActionCardTitleDisplay.ACTION | ActionCardTitleDisplay.ACTION_COMPACT;
}

export const ActionTitle = ({
  action,
  groupedActions,
  title,
  actionTypeLabel,
  candidate,
  setActionPopoverState,
  titleDisplay = ActionCardTitleDisplay.ACTION,
}: IActionTitleProps) => {
  const grouped = groupedActions.length > 0;
  const { timeZone } = useI18nContext();
  const { user } = useGlobalContext();
  const theme = useTheme();
  const startTime =
    action.executionDate || action.executionPeriod?.startDate
      ? formatTimeFromDate(
          new Date(action.executionDate || action.executionPeriod?.startDate || new Date()),
          undefined,
          timeZone
        )
      : action.deadlineDate
        ? formatTimeFromDate(new Date(action.deadlineDate), undefined, timeZone)
        : "";

  const endTime =
    action.endDate || action.executionPeriod?.endDate
      ? formatTimeFromDate(
          new Date(action.endDate || action.executionPeriod?.endDate || new Date()),
          undefined,
          timeZone
        )
      : "";
  const redirectUrl = `/v2/candidate/${candidate?._id}?organisationId=${user.organisation._id}`;

  return (
    <Stack sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        className={classes.title}
        variant="subtitle1SemiBold"
        onClick={
          setActionPopoverState
            ? (evt) => {
                setActionPopoverState({
                  anchorEl: evt.currentTarget,
                  actions: [action, ...groupedActions],
                });
              }
            : undefined
        }
        sx={setActionPopoverState ? { cursor: "pointer" } : undefined}
        noWrap
      >
        {grouped && <>{groupedActions.length + 1}&nbsp;</>}
        <span
          className={grouped ? classes.uncapitalize : classes.capitalize}
          style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, overflow: "hidden" }}
        >
          <ReminderStatusIcon status={action.appointmentReminderStatus} />
          {titleDisplay === ActionCardTitleDisplay.ACTION && startTime && endTime && (
            <Typography component="span" variant="subtitle1">
              {startTime}
              {endTime && startTime ? ` à ${endTime}` : ""}
              {" :\u00A0"}
            </Typography>
          )}
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
            {grouped || !title ? actionTypeLabel : title}
          </span>
          {candidate && (
            <span style={{ flexShrink: 0 }}>
              {" - "}
              <Link
                style={{ color: theme.palette.primary.main, textDecoration: "none" }}
                to={redirectUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(evt) => evt.stopPropagation()}
              >
                <Typography
                  variant="subtitle1SemiBold"
                  color="primary"
                  data-hj-suppress="true"
                  sx={{ textDecoration: "underline" }}
                >
                  {candidate.firstName} {candidate.lastName}
                </Typography>
              </Link>
            </span>
          )}
        </span>
      </Typography>
      {action.externalIntegrations
        ?.filter((integration) => !integration.deleted)
        .map((integration) => (
          <ExternalIntegrationStatusLabel
            key={integration.provider}
            provider={integration.provider}
            status={integration.status}
            error={integration.error}
          />
        ))}
    </Stack>
  );
};
